import { createElement as h } from "react";
import { DW, DH, HUB } from "@/lib/sky";
import { icon, starfield } from "@/components/mh/SkySvg";
import { WAVE_DASH } from "@/components/mh/SkyParts";

// Built without JSX on purpose: the preview's visual editor wraps dynamic JSX children in <span>, which breaks SVG rendering.
const GLYPH = {
  "Human-led": (x, y, c) => h("path", { d: `M${x - 6} ${y + 3} Q${x} ${y - 7} ${x + 6} ${y + 3}`, fill: "none", stroke: c, strokeWidth: 1.3 }),
  "Human-assisted": (x, y, c) => h("g", { stroke: c, strokeWidth: 1.3, fill: "none" },
    h("line", { x1: x - 8, y1: y, x2: x - 3, y2: y }), h("circle", { cx: x, cy: y, r: 2.6 }), h("line", { x1: x + 3, y1: y, x2: x + 8, y2: y })),
  "Fully autonomous": (x, y, c) => h("path", { d: `M${x - 5} ${y + 4} L${x} ${y - 5} L${x + 5} ${y + 4}`, fill: "none", stroke: c, strokeWidth: 1.3 }),
};
const LINK_COLOR = { req: "#00E5FF", en: "#D4FF3A", rel: "rgba(232,232,238,0.55)" };

const nodeEl = (n, p) => {
  const st = p.status(n), r = p.radius(n), on = p.hover === n.slug, t = n.tailored;
  const dim = !p.match(n) ? 0.12 : p.lit && !p.lit.has(n.slug) ? 0.25 : st === "inactive" ? 0.45 : 1;
  const c = st === "running" ? n.hex : "#E8E8EE";
  return h("g", { key: n.slug, className: "mh-rise", style: { animationDelay: `${150 + n.i * 26}ms` } },
    h("g", { opacity: dim, style: { transition: "opacity 300ms ease" }, className: "cursor-pointer",
      onMouseEnter: () => p.onHover(n), onMouseLeave: () => p.onHover(null), onClick: () => p.onNode(n),
      "data-testid": `dept-node-${n.slug}`, "data-status": st, "data-size": Math.round(r) },
      st === "running" ? h("circle", { cx: n.x, cy: n.y, r: r + 14, fill: n.hex, opacity: 0.2, filter: "url(#dept-glow)" }) : null,
      st === "needs" ? h("circle", { cx: n.x, cy: n.y, r: r + 7, fill: "none", stroke: "#FFB020", strokeOpacity: 0.5, strokeWidth: 5, filter: "url(#dept-glow)" }) : null,
      p.sel === n.slug ? h("circle", { cx: n.x, cy: n.y, r: r + 10, fill: "none", stroke: "#D4FF3A", strokeWidth: 1.5, className: "mh-hub-pulse", "data-testid": "dept-node-selected" }) : null,
      t?.enabled && t.wave ? h("circle", { cx: n.x, cy: n.y, r: r + 4, fill: "none", stroke: "#E8E8EE", strokeOpacity: 0.55, strokeDasharray: WAVE_DASH[t.wave] }) : null,
      h("circle", { cx: n.x, cy: n.y, r, fill: "#0A0A0F", stroke: on ? n.hex : "#E8E8EE", strokeOpacity: on ? 1 : 0.7, strokeWidth: on ? 1.8 : 1,
        strokeDasharray: st === "inactive" ? "2 3" : undefined, style: { transition: "stroke 200ms ease" } }),
      GLYPH[n.autonomy]?.(n.x, n.y, c),
      h("text", { x: n.x, y: n.y + r + 17, textAnchor: "middle", fontSize: 13, fill: "#D4D4DE" }, n.name)));
};

const chainLines = (p) => {
  const out = [];
  p.labels.forEach((lb) => {
    const ns = p.nodes.filter((n) => n.stage === lb.stage);
    ns.forEach((n, i) => {
      const a = i ? ns[i - 1] : HUB;
      out.push(h("line", { key: `c-${n.slug}`, x1: a.x, y1: a.y, x2: n.x, y2: n.y, stroke: p.dept.hex, strokeOpacity: p.lit ? 0.08 : 0.3 }));
      out.push(h("circle", { key: `d-${n.slug}`, cx: (a.x + n.x) / 2, cy: (a.y + n.y) / 2, r: 2, fill: p.dept.hex, opacity: p.lit ? 0.2 : 0.9 }));
    });
  });
  return out;
};

const hubEl = (p) => h("g", { key: "hub", "data-testid": "dept-hub" },
  h("circle", { cx: HUB.x, cy: HUB.y, r: 90, fill: p.dept.hex, opacity: 0.28, filter: "url(#dept-glow-lg)" }),
  h("circle", { cx: HUB.x, cy: HUB.y, r: 42, fill: "#0A0A0F", stroke: p.dept.hex, strokeWidth: 2 }),
  icon(p.dept.slug, HUB.x, HUB.y, 28, p.dept.hex),
  h("text", { x: HUB.x, y: HUB.y + 82, textAnchor: "middle", fontSize: 34, letterSpacing: 7, fill: "#ECECF2", fontFamily: "Instrument Serif" }, p.dept.name.toUpperCase()),
  h("text", { x: HUB.x, y: HUB.y + 104, textAnchor: "middle", fontSize: 11, letterSpacing: 3, fill: "#9A9AAE", fontFamily: "JetBrains Mono" }, p.dept.tagline || ""));

const hoverLines = (p) => {
  if (!p.hover) return [];
  const src = p.nodes.find((n) => n.slug === p.hover);
  const out = [];
  ["rel", "req", "en"].forEach((k) => p.idx[k](p.hover).forEach((s) => {
    const t = p.nodes.find((n) => n.slug === s);
    if (src && t) out.push(h("line", { key: `h-${k}-${s}`, x1: src.x, y1: src.y, x2: t.x, y2: t.y, stroke: LINK_COLOR[k], strokeWidth: 1.4,
      strokeDasharray: k === "rel" ? "4 4" : undefined, "data-testid": `dept-link-${k}-${s}` }));
  }));
  return out;
};

export function buildDeptSky(p) {
  const kids = [
    h("g", { key: "stars" }, ...starfield(DW, DH, 160, 11)),
    h("text", { key: "bg", x: DW / 2, y: DH / 2 - 10, textAnchor: "middle", fontSize: 340, fill: "#FFFFFF", fillOpacity: 0.035, fontFamily: "Instrument Serif", letterSpacing: 24 }, p.dept.name.toUpperCase()),
    ...chainLines(p), ...hoverLines(p),
    ...p.labels.map((lb) => h("g", { key: `lb-${lb.stage}`, "data-testid": `dept-stage-${lb.stage.toLowerCase()}` },
      h("text", { x: lb.x, y: lb.y, textAnchor: "middle", fontSize: 26, letterSpacing: 6, fill: "#E8E8EE", fillOpacity: 0.9, fontFamily: "Instrument Serif" }, lb.stage.toUpperCase()),
      h("text", { x: lb.x, y: lb.y + 18, textAnchor: "middle", fontSize: 10, letterSpacing: 3, fill: "#9A9AAE", fontFamily: "JetBrains Mono" }, `${lb.count} JOBS`))),
    hubEl(p),
    ...p.nodes.map((n) => nodeEl(n, p)),
  ];
  return h("svg", { viewBox: `0 0 ${DW} ${DH}`, className: "absolute inset-0 w-full h-full", "data-testid": "dept-sky-svg" },
    h("defs", null, h("filter", { id: "dept-glow", x: "-100%", y: "-100%", width: "300%", height: "300%" }, h("feGaussianBlur", { stdDeviation: 8 })),
      h("filter", { id: "dept-glow-lg", x: "-150%", y: "-150%", width: "400%", height: "400%" }, h("feGaussianBlur", { stdDeviation: 26 }))),
    h("g", { className: "mh-zoom-in" }, ...kids));
}
